import React from 'react';
import PageLayout from '@/components/Layouts/PageLayout';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import Divider from '@/shared/Divider';
import MailIcon from '../Icons/MailIcon';
import PhoneIcon from '../Icons/PhoneIcon';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { getEnvValue } from '@/utils/envUtils';
import LocationIcon from '../Icons/LocationIcon';
import classNames from 'classnames';
import useScreen from '@/hooks/useScreen';

interface CorporateOffice {
  id: number;
  attributes: {
    address: string;
    pincode: string;
    servicePhone: string;
    salePhone: string;
    saleEmail: string;
    serviceEmail: string;
    latitude: number;
    longitude: number;
  };
}

interface CorporateOfficePageProps {
  initialLocations: CorporateOffice[];
  googleMapApiKey: string;
}

const API_KEY = getEnvValue('GOOGLE_MAP_API_KEY');

const CorporateOfficePage: React.FC<CorporateOfficePageProps> = ({
  initialLocations,
  googleMapApiKey,
}) => {
  const { isMobile, isTablet } = useScreen();
  const isMobileOrTablet = isMobile || isTablet;
  const officeData = initialLocations[0];
  const {
    latitude: lat,
    longitude: lng,
    pincode,
    salePhone,
    servicePhone,
    address,
    saleEmail,
    serviceEmail,
  } = officeData?.attributes ?? {};
  const latLong = lat && lng ? { lat, lng } : null;

  const renderMap = () => (
    <Div
      className={classNames('relative rounded-cs overflow-hidden', {
        'w-[876px] h-[500px]': !isMobileOrTablet,
        'w-full h-[300px]': isMobileOrTablet,
      })}
    >
      {latLong && (
        <APIProvider apiKey={googleMapApiKey}>
          <Map
            defaultZoom={14}
            defaultCenter={latLong}
            center={latLong}
            gestureHandling={'none'}
            streetViewControl={false}
            zoomControl={false}
            fullscreenControl={false}
            mapTypeControl={false}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            }}
          >
            <Div
              className="absolute"
              style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            >
              <Marker position={latLong} />
            </Div>
          </Map>
        </APIProvider>
      )}
    </Div>
  );

  const contactInfo = (
    <Div
      className={classNames('flex flex-col items-start gap-s8', {
        'flex-[1_0_0]': !isMobileOrTablet,
        'w-full': isMobileOrTablet,
      })}
    >
      <Div className="flex items-start gap-s4 self-stretch">
        <Div className="w-10 h-10 flex items-start justify-center">
          <LocationIcon />
        </Div>

        <Div className="flex flex-col items-start gap-s3 flex-[1_0_0]">
          <Div className="flex items-center self-stretch">
            <h3 className="text-hd-4 text-cti-primary">Our Location</h3>
          </Div>

          <Div
            className={classNames('text-body-base text-cti-secondary', {
              'w-[347px]': !isMobileOrTablet,
              'w-full': isMobileOrTablet,
            })}
          >
            {address}
            {pincode && ` - ${pincode}`}
          </Div>
        </Div>
      </Div>

      <Divider
        className={classNames('h-[1px]', {
          'w-[449px]': !isMobileOrTablet,
          'w-full': isMobileOrTablet,
        })}
      />

      <Div className="flex items-start gap-s4 self-stretch">
        <Div className="w-10 h-10 flex items-start justify-center">
          <PhoneIcon />
        </Div>

        <Div className="flex flex-col items-start gap-s3 flex-[1_0_0]">
          <Div className="flex items-center self-stretch">
            <h3 className="text-hd-4 text-cti-primary">Contact us by Phone</h3>
          </Div>

          <Div className="text-body-base text-cti-secondary">
            Service: {servicePhone} <br />
            Sales Enquiry: {salePhone}
          </Div>
        </Div>
      </Div>

      <Divider
        className={classNames('h-[1px]', {
          'w-[449px]': !isMobileOrTablet,
          'w-full': isMobileOrTablet,
        })}
      />

      <Div className="flex items-start gap-s4 self-stretch">
        <Div className="w-10 h-10 flex items-start justify-center">
          <MailIcon />
        </Div>

        <Div className="flex flex-col items-start gap-s3 flex-[1_0_0]">
          <Div className="flex items-center self-stretch">
            <h3 className="text-hd-4 text-cti-primary">Email Our Team</h3>
          </Div>

          <Div className="text-body-base text-cti-secondary">
            Online Sales: {saleEmail}
            <br />
            Service: {serviceEmail}
          </Div>
        </Div>
      </Div>
    </Div>
  );

  return (
    <Container
      className={classNames('flex items-start gap-s8', {
        'w-[1240px] h-[733px] flex-row': !isMobileOrTablet,
        'w-full flex-col': isMobileOrTablet,
      })}
    >
      {contactInfo}
      {renderMap()}
    </Container>
  );
};

export default CorporateOfficePage;
